import { LitElement, css, html } from 'lit'
import { customElement, property } from 'lit/decorators.js'

export type ErrorMessageListState = 'valid' | 'invalid' | 'neutral' | 'loading'

/**
 * An element specifically for being more explicit and reusable than <div style="display: contents;">
 */
@customElement('ep-error-message-list')
export class EPErrorMessageList extends LitElement {

  @property({ attribute: 'positive-message', reflect: true })
  positiveMessage = '✅ ABC is valid'

  @property({ attribute: 'negative-message', reflect: true })
  negativeMessage = '🛑 ABC is invalid'

  @property({ attribute: 'neutral-message', reflect: true })
  neutralMessage = ''

  @property({ attribute: 'loading-message', reflect: true })
  loadingMessage = 'Loading...'

  @property({ attribute: false })
  state: ErrorMessageListState = 'neutral'

  @property({ attribute: false })
  messages: string[] = []

  render() {
    switch (this.state) {
      case 'neutral': {
        return html`
        <div class="title">
          <h2>${this.neutralMessage}</h2>
        </div>
        `
      }
      case 'loading': {
        return html`
        <div class="title" style="display: inline-flex; gap: 5px;">
          <fluent-progress-ring style="height: 24px;"></fluent-progress-ring>
          <h2>${this.loadingMessage}</h2>
        </div>
        `
      }
      case 'valid': {
        return html`
        <div class="title">
          <h2>${this.positiveMessage}</h2>
        </div>
        `
      }
      case 'invalid': {
        return html`
        <fieldset style="border: var(--border-error);">
          <legend class="title">
            <h2>${this.negativeMessage}</h2>
          </legend>
          <ul class="messages">
            ${this.messages.map(msg => (
              html`<li>${msg}</li>`
            ))}
          </ul>
        </fieldset>
        `
      }
    }
  }

  static styles = css`
    :host {
      font-family: var(--font-prose);
      font-weight: 400;
      font-size: 12px;
      white-space: normal; /* counter-acts quill */
      line-height: 1.5;
      color: var(--color-text-soft-black);
      box-sizing: border-box;

      --color-text-soft-black: #24292F;
      --color-text-danger: #d1242f;
      --color-text-muted: hsla(0, 0%, 15%, 0.7);
      --color-border: hsla(218, 32%, 78%, 0.5);
      --border-error: 2px dashed var(--color-text-danger);
      --font-mono: ui-monospace, SFMono-Regular, SF Mono, Menlo, "Cascadia Mono", Consolas, Liberation Mono, monospace;
      --font-prose: ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,"Noto Sans",sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji";
    }

    .title h2 {
      font-size: 1.15em;
      margin: 0;
      padding: 0;
      padding-bottom: 0;
    }

    .messages li {
      font-family: var(--font-mono);
      color: var(--color-text-danger);
      font-weight: 500;
    }
  `
}

declare global {
  interface HTMLElementTagNameMap {
    'ep-error-message-list': EPErrorMessageList
  }
}
